import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton, MainSectionPanel } from '../../../components/common';
import { PermissionGuard } from '../../../auth/components/PermissionGuard';
import { useGuiaMateriaPrimaStore } from './store/useGuiaMateriaPrimaStore';
import { EditDERC } from '../derc/components';
import ProductTypeSelectionModal from './components/ProductTypeSelectionModal';
import { type DERCRequest } from '../derc/types/derc';
import { useDERCStore } from '../derc/store';
import type { EstadoGuia } from './types';

const estadoLabels: Record<EstadoGuia, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const estadoColors: Record<EstadoGuia, 'warning' | 'success' | 'error'> = {
  'PEN': 'warning',
  'APR': 'success',
  'ANU': 'error',
};

const GuiaMateriaPage: React.FC = () => {
  const navigate = useNavigate();
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [showDercModal, setShowDercModal] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState<1 | 2>(1);
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    guias,
    loading,
    error,
    fetchGuias,
    clearError,
  } = useGuiaMateriaPrimaStore();

  // Filter guias by tab
  // When Parte (Paso 3) is APR, the guía moves from APROBADAS to LISTA
  const filteredGuias = guias.filter((guia) => {
    switch (selectedTab) {
      case 0: // Pendientes
        return guia.estado === 'PEN';
      case 1: // Aprobadas (excluir si su Parte está aprobado)
        return guia.estado === 'APR' && !guia.parteAprobado;
      case 2: // Anuladas
        return guia.estado === 'ANU';
      case 3: // Lista (todos)
        return true;
      default:
        return true;
    }
  });

  const { saveDERC } = useDERCStore();

  const columns: GridColDef[] = [
    {
      field: 'numeroComprobante',
      headerName: '#',
      width: 80,
      type: 'number'
    },
    {
      field: 'idOrigenProducto',
      headerName: 'Tipo',
      width: 60,
      renderCell: (params: GridRenderCellParams) => {
        const tipo = params.value === 1 ? 'A' : params.value === 2 ? 'B' : '-';
        return (
          <Chip
            label={tipo}
            size="small"
            color={params.value === 1 ? 'primary' : 'error'}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'numeroDocumento',
      headerName: 'N° DERC',
      width: 150,
    },
    {
      field: 'fechaExtraccion',
      headerName: 'Fecha',
      width: 100,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'proveedor',
      headerName: 'Proveedor',
      width: 180,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const isTypeB = params.row.idOrigenProducto === 2;
        if (isTypeB) return 'N/A';
        return params.value || '';
      },
    },
    {
      field: 'duenios',
      headerName: 'Dueño(s)',
      width: 150
    },
    {
      field: 'encargados',
      headerName: 'Encargado(s)',
      width: 150
    },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 70,
      type: 'number'
    },
    {
      field: 'subLoteCantidad',
      headerName: 'Sub Lote',
      width: 90,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const estado = (params.value as EstadoGuia) || 'PEN';
        return (
          <Chip
            label={estadoLabels[estado]}
            color={estadoColors[estado]}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isPendiente = params.row.estado === 'PEN';
        const isAprobada = params.row.estado === 'APR';
        const isAnulada = params.row.estado === 'ANU';
        const idGuiaProduccion = params.row.idGuiaProduccion;
        const hasGuiaProduccion = idGuiaProduccion != null;

        // Anuladas: sin acciones
        if (isAnulada) return null;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPendiente ? (
              // Pendientes: Lápiz para editar
              <PermissionGuard permission="PRODUCCION_EDITAR">
              <GridEditButton
                onClick={() => handleEdit(params.row.id)}
                tooltip="Editar"
                fontSize="small"
                ariaLabel={`Editar guía ${params.row.numeroDocumento}`}
              />
              </PermissionGuard>
            ) : isAprobada ? (
              <>
                {/* Aprobadas: Ojo para ver/cambiar estado */}
                <Tooltip title="Ver / Cambiar estado">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEdit(params.row.id)}
                    aria-label={`Ver guía ${params.row.numeroDocumento}`}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* Flecha para generar/ver guía de producción */}
                {hasGuiaProduccion ? (
                  <Tooltip title="Ver Guía de Producción">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleVerGuiaProduccion(idGuiaProduccion)}
                      aria-label={`Ver guía de producción ${idGuiaProduccion}`}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <PermissionGuard permission="PRODUCCION_EDITAR">
                  <Tooltip title="Generar Guía de Producción">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleGenerarGuiaProduccion(params.row.id, params.row.idDetalle)}
                      aria-label={`Generar guía de producción para ${params.row.numeroDocumento}`}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  </PermissionGuard>
                )}
              </>
            ) : null}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchGuias();
  }, [fetchGuias]);

  // Cleanup effect: close modals when component unmounts
  useEffect(() => {
    return () => {
      setShowProductTypeModal(false);
      setShowDercModal(false);
    };
  }, []);

  const handleAdd = () => {
    setShowProductTypeModal(true);
  };

  const handleCloseProductTypeModal = () => {
    setShowProductTypeModal(false);
  };

  const handleSelectProductType = (productType: 'A' | 'B') => {
    // Convert product type to number (A=1, B=2)
    const typeNumber = productType === 'A' ? 1 : 2;
    setSelectedProductType(typeNumber);

    // Close product type modal and show DERC modal
    setShowProductTypeModal(false);
    setShowDercModal(true);
  };

  const handleEdit = (id: number) => {
    navigate(`/produccion/guias-materia-prima/${id}`);
  };

  const handleGenerarGuiaProduccion = (idGuiaMateriaPrima: number, idDetalle?: number) => {
    // Navigate to new guia produccion form with the guia materia prima ID and optional detalle ID
    let url = `/produccion/guias-produccion/new?idGuiaMateriaPrima=${idGuiaMateriaPrima}`;
    if (idDetalle) {
      url += `&idDetalle=${idDetalle}`;
    }
    navigate(url);
  };

  const handleVerGuiaProduccion = (idGuiaProduccion: number) => {
    // Navigate to edit the existing guia produccion
    navigate(`/produccion/guias-produccion/${idGuiaProduccion}`);
  };

  const handleCloseDercModal = () => {
    setShowDercModal(false);
  };

  const handleSaveDerc = async (dercRequest: DERCRequest) => {
    try {
      const savedDerc = await saveDERC(dercRequest);

      if (savedDerc) {
        // Close the DERC modal
        setShowDercModal(false);

        // Navigate to new guia form with the newly created DERC ID and product type in the URL
        const productTypeParam = selectedProductType === 1 ? 'A' : 'B';
        navigate(`/produccion/guias-materia-prima/new?dercId=${savedDerc.id}&productType=${productTypeParam}`);
      }
    } catch (error) {
      console.error('Error saving DERC:', error);
      // Don't close the modal so user can see the error and try again
    }
  };



  const handleCloseError = () => {
    clearError();
  };

  return (
    <MainSectionPanel>
      <PageTitle
        title="Guías de Materia Prima"
        subtitle="Gestión de Guías de Recepción de Materia Prima"
        flowStep={1}
        actions={
          <PermissionGuard permission="PRODUCCION_EDITAR">
            <AddButton onClick={handleAdd} text='Nueva Guía' />
          </PermissionGuard>
        }
      />

      <ListLayoutPanel>

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(_e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Pendientes (${guias.filter(g => g.estado === 'PEN').length})`} />
            <Tab label={`Aprobadas (${guias.filter(g => g.estado === 'APR').length})`} />
            <Tab label={`Anuladas (${guias.filter(g => g.estado === 'ANU').length})`} />
            <Tab label={`Lista (${guias.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredGuias}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.subLoteIndex ? `${row.id}-${row.subLoteIndex}` : row.id}
            pageSize={25}
          />
        )}
      </ListLayoutPanel>

      {/* Product Type Selection Modal */}
      <ProductTypeSelectionModal
        open={showProductTypeModal}
        onClose={handleCloseProductTypeModal}
        onSelectType={handleSelectProductType}
      />

      {/* DERC Modal */}
      <EditDERC
        open={showDercModal}
        dercId={null}
        onClose={handleCloseDercModal}
        onSave={handleSaveDerc}
        title={`Crear DERC ${selectedProductType === 1 ? 'A' : 'B'} para Guía de Materia Prima`}
        defaultIdOrigenProducto={selectedProductType}
      />

    </MainSectionPanel>
  );
};

export default GuiaMateriaPage;